/**
 * 爱微圈导航 · 共享基础：工具 / API / 状态
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { closeModal, createCloudBackup, loadBackupListInto, modalConfirm, openModal, runDataRepair, setupDataButtons, showBackupList, showModalError, showQrSync } from './modal.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export const $ = (s, p = document) => p.querySelector(s);

export const $$ = (s, p = document) => [...p.querySelectorAll(s)];


export function esc(str) {
    return String(str ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

export function escAttr(str) { return esc(str); }


export function safeUrl(url) {
    const u = String(url ?? '').trim();
    if (/^https?:\/\//i.test(u)) return u;
    if (/^[\w.-]+\.[a-z]{2,}(\/|$|\?|#)/i.test(u)) return 'https://' + u;
    return '';
}


export function debounce(fn, wait) {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}


export function randomColor() {
    const colors = ['#5B8DD4', '#21A95F', '#E1251B', '#FF6A00', '#9B59B6', '#00B5D8', '#F59E0B', '#EF4444', '#8B5CF6', '#10B981'];
    return colors[Math.floor(Math.random() * colors.length)];
}


export function toast(msg, type = 'info') {
    const wrap = $('#toastWrap');
    const el = document.createElement('div');
    el.className = 'toast ' + type;
    const ico = type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ';
    el.innerHTML = `<span class="t-ico">${ico}</span><span>${esc(msg)}</span>`;
    wrap.appendChild(el);
    setTimeout(() => { el.classList.add('out'); setTimeout(() => el.remove(), 320); }, 2600);
}

/* ---------- API ---------- */

/* 接口路径一律「根绝对」而非相对（2026-09-20 修复）：
 * 相对写法 `api/auth.php` 以「当前页面 URL 的目录」为基准解析。nginx 用
 * `try_files $uri $uri/ /index.php?$query_string` 兜底，任意路径（/foo/、/foo/bar/）
 * 都会返回首页且状态 200，但地址栏仍是那个路径 —— 此时相对接口会被解析成
 * /foo/bar/api/auth.php → 404，表现为「页面看着正常，但一登录/注册就报错」。
 * 与 render.js / data.js 里既有的 '/api/icon_proxy.php' 写法保持一致。 */
export const API = {
    auth: '/api/auth.php', shortcuts: '/api/shortcuts.php', settings: '/api/settings.php',
    todos: '/api/todos.php', notes: '/api/notes.php', websites: '/api/websites.php',
    suggest: '/api/suggest.php', sync: '/api/sync.php', wallpaper: '/api/wallpaper.php',
    weather: '/api/weather.php', engines: '/api/engines.php', site_info: '/api/site_info.php',
    donations: '/api/donations.php', visit: '/api/visit.php', click: '/api/click.php', feedback: '/api/feedback.php',
};

/* P1-11 超时熔断：所有 API 请求默认 8s 超时。
 * opts.timeoutMs 可覆盖（传 0 表示不限时）；外部传入的 signal（如分页预取的取消）
 * 与超时 signal 合并，任一触发即中断。 */

export const API_TIMEOUT_MS = 8000;

/** 是否游客（未登录）。登录限定功能入口据此主动拦截（2026-09-04 策略：除浏览导航外均需登录） */
export function isGuest() { return !state.user || !!state.user.is_guest; }

/** 游客拦截：若是游客则弹登录框并返回 true（调用方直接 return） */
export function gateGuest() { if (!isGuest()) return false; showAuthModal('login'); return true; }


export function fetchWithTimeout(url, fetchOpts = {}, timeoutMs = API_TIMEOUT_MS) {
    const ctrl = new AbortController();
    const external = fetchOpts.signal || null;
    const onExternalAbort = () => ctrl.abort();
    let timer = null;
    if (timeoutMs > 0) timer = setTimeout(() => ctrl.abort(), timeoutMs);
    if (external) {
        if (external.aborted) ctrl.abort();
        else external.addEventListener('abort', onExternalAbort, { once: true });
    }
    fetchOpts.signal = ctrl.signal;
    return fetch(url, fetchOpts).finally(() => {
        if (timer !== null) clearTimeout(timer);
        if (external) external.removeEventListener('abort', onExternalAbort);
    });
}


/* 静默换发一次 CSRF 令牌（2026-09-20）。
 * 场景：浏览器 / Service Worker / bfcache 里是「旧 HTML」，页内令牌属于一个已经
 * 过期或被换发的会话 —— 此时第一次提交必然 403。老逻辑遇到 403 直接
 * `location.reload()`，会**把用户已经填好的邮箱密码一起抹掉**，逼他重填一遍，
 * 对外表现就是「第一次注册/登录失败，第二次才成功」。
 * 这里改为先取一次新令牌再原位重发，用户无感、也不丢表单。 */
export async function refreshCsrf() {
    try {
        const res = await fetchWithTimeout(API.auth + '?action=me', { credentials: 'same-origin' }, API_TIMEOUT_MS);
        if (!res.ok) return false;
        const d = await res.json();
        if (d && typeof d.csrf === 'string' && d.csrf) { state.csrf = d.csrf; return true; }
    } catch (_) { /* 网络异常由调用方的失败分支兜底 */ }
    return false;
}


/* 非 JSON 响应（nginx 404/502 的 HTML 错误页、反代拦截页等）统一给出可读提示。
 * 之前一律抛「解析响应失败」，用户只看到四个字，无法区分是接口 404、服务器 502
 * 还是网关拦截。这里按状态码分类，并把状态码挂到 error.status 供调用方分支。 */
function httpError(status, text) {
    let msg;
    if (status === 404) msg = '接口不存在（404），请确认站点部署在域名根目录后重试';
    else if (status === 502 || status === 504) msg = '网关异常（' + status + '），请稍后重试';
    else if (status >= 500) msg = '服务器暂时不可用（' + status + '），请稍后重试';
    else if (status === 403) msg = '访问被拒绝（403），请刷新页面后重试';
    else msg = '服务器返回了非预期内容（HTTP ' + status + '）';
    const err = new Error(msg);
    err.status = status;
    err.raw = String(text || '').slice(0, 200);
    return err;
}

/* 会话过期（403）自动刷新一次换新 CSRF。加 10s 一次性守卫：
 * 若刚刷新过仍 403，说明刷新拿不到新令牌（SW / 浏览器缓存了旧 HTML 等），
 * 继续刷新只会陷入死循环 —— 此时改为提示用户手动硬刷新。 */
function tryReloadForCsrf() {
    const KEY = 'csrf_reload_at';
    let last = 0;
    try { last = parseInt(sessionStorage.getItem(KEY) || '0', 10) || 0; } catch (_) { last = 0; }
    if (Date.now() - last > 10000) {
        try { sessionStorage.setItem(KEY, String(Date.now())); } catch (_) {}
        toast('会话已过期，正在刷新…', 'error');
        setTimeout(() => location.reload(), 900);
        return;
    }
    toast('会话校验失败，请手动刷新页面（Ctrl/Cmd + R）后重试', 'error');
}


export async function apiGet(url, opts = {}) {
    let res;
    try {
        res = await fetchWithTimeout(url, { credentials: 'same-origin', signal: opts.signal || null }, opts.timeoutMs ?? API_TIMEOUT_MS);
    } catch (e) {
        if (e && e.name === 'AbortError') {
            // 外部 signal 主动取消（如切分类废弃旧请求）：静默上抛，不弹提示
            if (opts.signal && opts.signal.aborted) throw e;
            // 否则即超时中断
            toast('请求超时，请检查网络后重试', 'error');
            throw new Error('请求超时');
        }
        toast('网络连接失败，请检查网络后重试', 'error');
        throw new Error('网络连接失败');
    }
    if (res.status === 429) { toast('操作过于频繁，请稍后再试', 'error'); throw new Error('操作过于频繁，请稍后再试'); }
    const text = await res.text();
    if (!text || text.trim() === '') {
        if (!res.ok) throw httpError(res.status, text);
        throw new Error('服务器返回空响应');
    }
    let data;
    try { data = JSON.parse(text); } catch(e) { throw httpError(res.status, text); }
    if (!res.ok) {
        // 游客触碰登录限定功能：弹登录框（2026-09-04 策略）
        if (data && data.need_login) { toast(data.error || '登录后才能使用此功能', 'error'); showAuthModal('login'); throw new Error('need_login'); }
        if (res.status === 401) { toast('请先登录', 'error'); }
        else if (res.status === 403) { tryReloadForCsrf(); throw new Error('会话已过期，请刷新页面后重试'); }
        else if (res.status >= 500) { toast('服务器暂时不可用，请稍后重试', 'error'); }
        else { toast(data.error || '请求失败', 'error'); }
        throw new Error(data.error || '请求失败');
    }
    return data;
}


export async function apiPost(url, body, _retriedCsrf = false) {
    let res;
    try {
        res = await fetchWithTimeout(url, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': state.csrf },
            body: JSON.stringify(body || {}),
        }, API_TIMEOUT_MS);
    } catch (e) {
        if (e && e.name === 'AbortError') {
            toast('请求超时，请检查网络后重试', 'error');
            throw new Error('请求超时');
        }
        toast('网络连接失败，请检查网络后重试', 'error');
        throw new Error('网络连接失败');
    }
    if (res.status === 429) { toast('操作过于频繁，请稍后再试', 'error'); throw new Error('操作过于频繁，请稍后再试'); }
    const text = await res.text();
    if (!text || text.trim() === '') {
        if (!res.ok) throw httpError(res.status, text);
        throw new Error('服务器返回空响应');
    }
    let data;
    try { data = JSON.parse(text); } catch(e) { throw httpError(res.status, text); }
    if (!res.ok) {
        // 游客触碰登录限定功能：弹登录框（2026-09-04 策略）。须在 CSRF-403 刷新之前判断。
        if (data && data.need_login) { toast(data.error || '登录后才能使用此功能', 'error'); showAuthModal('login'); throw new Error('need_login'); }
        if (res.status === 403) {
            // CSRF 校验失败时服务端尚未执行任何业务逻辑（api_boot() 最先校验），
            // 故「换令牌 + 原样重发一次」是安全的，且不会丢用户已填写的表单。
            if (!_retriedCsrf && await refreshCsrf()) return apiPost(url, body, true);
            tryReloadForCsrf();
            throw new Error('会话已过期，请刷新页面后重试');
        }
        if (res.status === 401) { toast('请先登录', 'error'); }
        else if (res.status >= 500) { toast('服务器暂时不可用，请稍后重试', 'error'); }
        else { toast(data.error || '操作失败', 'error'); }
        const err = new Error(data.error || '操作失败');
        err.status = res.status;
        throw err;
    }
    return data;
}

/* ---------- 状态 ---------- */

export const bootData = window.__INITIAL__ || {};

export const state = {
    csrf: bootData.csrf || '',
    user: bootData.user || null,
    site: bootData.site || {},
    settings: {},
    shortcuts: [],
    pages: [1],
    currentPage: 1,
    shortcutCache: {},
    categories: [],
    currentCat: 0,
    websiteCache: {},
    _websiteAbort: null,
    todos: [],
    todoFilter: 'all',     // 待办筛选：all / active / done
    notes: [],
    noteFilter: '',        // 便签搜索关键字（纯前端过滤，不请求接口）
    engines: { builtin: [], custom: [] },
    weather: null,
    dragged: null,
    _newPages: new Set(),
    searchTab: 'web',
    bingWallpaper: null,
    organizeMode: false,
    selectedIds: new Set(),
};

/* 默认值单一事实来源是 config/defaults.php，由 index.php 注入 window.__DEFAULT_SETTINGS__。
 * 不再维护 JS 内联副本（历史上曾漂移 13 项，属于误导性死代码）。
 * 注入缺失时退化为空对象：加载路径会用 api/settings.php?action=get 的服务端合并结果填充。 */

export const DEFAULT_SETTINGS = window.__DEFAULT_SETTINGS__ || {};

/* 首屏缓存按用户隔离：避免同一浏览器切换账号后闪现上一个账号的快捷方式。
 * 键随 state.user 动态生成（启动时来自 __INITIAL__，登录/退出后跟随新身份）。
 * v2 → v3：旧共享键在 restoreCache 中顺手清理。 */

export function cacheKey() { return 'ceshi_cache_v3_u' + ((state.user && state.user.id) || 'anon'); }

export const CACHE_KEY_LEGACY = 'ceshi_cache_v2';


export function shouldAnimate() {
    if (state.settings.startup_animation !== '1') return false;
    if ((state.settings.animation_type || 'none') === 'none') return false;
    // 尊重系统减少动态偏好
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
    // 基础 CSS 动画能力探测
    const el = document.createElement('div');
    if (!('animation' in el.style) && !('webkitAnimation' in el.style)) return false;
    return true;
}

/* ---------- 时钟 ---------- */

